import { useLayoutEffect, useRef } from 'react'
import type { TenantAccessLevel } from '../lib/api'

const LEVEL_LABEL: Record<TenantAccessLevel, string> = {
  full: 'Full access', operational: 'Operational', validation: 'Validation-only',
}

/**
 * Pinned to the bottom of every screen while Parkify staff view the portal as a
 * tenant user (read-only). Fixed rather than in the layout flow so it cannot be
 * scrolled away and does not shift the sidebar. It reports its own height, which
 * wraps to more lines on a phone, so the shell can keep that strip free
 * (`--tenant-banner-h`): the sidebar footer, the phone menu and the dialogs end
 * above the banner instead of under it.
 */
export function ViewAsBanner({ name, level, full = false, onExit, onHeightChange }: {
  name: string
  level?: TenantAccessLevel
  /** "Sign in as tenant": full write, so the banner says so instead of read-only. */
  full?: boolean
  onExit: () => void
  /** Called with the banner's rendered height in px, before paint and on every resize. */
  onHeightChange?: (height: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !onHeightChange) return
    const report = () => onHeightChange(Math.ceil(el.getBoundingClientRect().height))
    report()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(report)
    observer.observe(el)
    return () => observer.disconnect()
  }, [onHeightChange])

  return (
    <div ref={ref} role="status" aria-label="View as" style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10000,
      background: 'var(--parkify-purple, #7F3F98)', color: '#fff',
      padding: '9px 16px calc(9px + env(safe-area-inset-bottom))',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap',
      fontSize: 13, fontWeight: 700, boxShadow: '0 -4px 16px rgba(0,0,0,0.18)',
    }}>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {full
          ? `Signed in as ${name} via Parkify support, full access. Changes are logged.`
          : `Viewing as ${name}${level ? ` (${LEVEL_LABEL[level] ?? level})` : ''}, read-only`}
      </span>
      <button onClick={onExit} style={{
        background: '#fff', color: 'var(--parkify-purple, #7F3F98)', border: 'none', borderRadius: 8,
        padding: '5px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
      }}>Exit</button>
    </div>
  )
}

export function ReadOnlyToast({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <div role="alert" style={{
      // Just above the banner, however many lines it wraps to.
      position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 'calc(var(--tenant-banner-h, 44px) + 20px)', zIndex: 10001,
      background: 'var(--color-gray-900, #231F20)', color: '#fff', borderRadius: 10,
      padding: '10px 16px', fontSize: 13, fontWeight: 600, maxWidth: 'calc(100vw - 32px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    }}>
      Read-only while viewing as this user. Nothing was changed.
    </div>
  )
}
